import { Skeleton, Tooltip, TooltipContent, TooltipTrigger, cn, useSidebar } from '@parcel/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ChevronRight, Coins } from 'lucide-react';
import { getSpecUsageSummary } from '../lib/spec-billing.js';
import { formatSpecBalanceUsd } from '../lib/spec-usage-guard.js';

export interface SidebarCreditsProps {
  workspaceId: string | undefined;
  /** Where the row goes; the caller owns workspace scoping. */
  usageHref: string;
  className?: string;
}

/**
 * The bought balance, at the bottom of the sidebar.
 *
 * Purchased credits only, not the monthly allowance: this is the number a member
 * topped up and watches go down. Shares its query key with the usage settings
 * page, so opening that page after reading this costs no second round trip.
 */
/** Absent on responses older than the field, which is not the same as a zero balance. */
function purchasedRemainingMicros(summary: { purchasedRemaining?: number }): number {
  return typeof summary.purchasedRemaining === 'number' &&
    Number.isFinite(summary.purchasedRemaining)
    ? summary.purchasedRemaining
    : 0;
}

export function SidebarCredits({ workspaceId, usageHref, className }: SidebarCreditsProps) {
  const { isMobile, state } = useSidebar();
  const query = useQuery({
    queryKey: ['spec-usage-summary', workspaceId],
    enabled: Boolean(workspaceId),
    staleTime: 60_000,
    retry: false,
    queryFn: () => getSpecUsageSummary(workspaceId ?? ''),
  });

  // A workspace that cannot read its own usage gets no row rather than a broken one.
  if (query.isError) return null;
  const remaining = query.data ? formatSpecBalanceUsd(purchasedRemainingMicros(query.data)) : null;

  const label = remaining ? `Credits: ${remaining}` : 'Credits';

  // Same tooltip contract as SidebarMenuButton: only shown on the collapsed icon rail.
  return (
    <>
      <div
        aria-hidden
        className="ml-4 mr-3 h-px bg-sidebar-border group-data-[collapsible=icon]:mx-0"
      />
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            to={usageHref}
            data-testid="sidebar-credits"
            // Named explicitly because the text is hidden when the sidebar collapses to icons.
            aria-label={label}
            className={cn(
              'flex items-center gap-2 overflow-hidden whitespace-nowrap rounded-md border border-sidebar-border bg-card px-3 py-1.5 shadow-sm transition-[background-color,box-shadow] hover:bg-sidebar-accent hover:shadow-md',
              // Collapsed icon rail: a bare size-8 square like the nav buttons above it.
              'group-data-[collapsible=icon]:size-8 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:border-0 group-data-[collapsible=icon]:bg-transparent group-data-[collapsible=icon]:p-2 group-data-[collapsible=icon]:shadow-none group-data-[collapsible=icon]:hover:bg-sidebar-accent',
              className,
            )}
          >
            <Coins
              className="hidden size-4 shrink-0 text-muted-foreground group-data-[collapsible=icon]:block"
              aria-hidden
            />
            <span className="flex min-w-0 flex-col gap-0.5 group-data-[collapsible=icon]:hidden">
              <span className="text-sm leading-4 text-muted-foreground">Credits</span>
              {remaining ? (
                <span className="flex items-center gap-1.5">
                  <Coins className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                  <span className="truncate text-base font-semibold leading-5 tabular-nums">
                    {remaining}
                  </span>
                </span>
              ) : (
                <Skeleton className="mt-1 h-4 w-16" />
              )}
            </span>
            <ChevronRight
              className="ml-auto size-4 shrink-0 text-muted-foreground group-data-[collapsible=icon]:hidden"
              aria-hidden
            />
          </Link>
        </TooltipTrigger>
        <TooltipContent side="right" align="center" hidden={state !== 'collapsed' || isMobile}>
          {label}
        </TooltipContent>
      </Tooltip>
    </>
  );
}
